import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Grid,
  Typography,
  Box,
} from '@material-ui/core';

import { selectItem, saveItem, clearSaveStatus } from './itemSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectGlobals } from '../app/appSlice';

import FilePicker from '../common/filePicker';
import {
  BooleanField,
  TextField,
} from '../common/fields';
import ActionBar from './ActionBar';
import DeleteList from '../common/deleteList';
import MainImage from '../itemFamily/MainImage';

const ItemPagePC1 = (props) => {
  const { itemOwnerId, itemId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { editableFields, item, saveStatus, saveError } = useSelector(selectItem);
  const { attributes, vehicleTypes, seasons } = useSelector(selectGlobals);
  const [ itemData, setItemData ] = useState(item);
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  // ID-name maps
  const itemLineNames = {
    vehicleTypes: {},
    seasons: {}
  };
  vehicleTypes.forEach(item => {
    itemLineNames.vehicleTypes[item.code] = item.name;
  });
  seasons.forEach(item => {
    itemLineNames.seasons[item.code] = item.name;
  });

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  const fieldIsDisabled = (field) => {
    return !editableFields.includes(field);
  }

  const handleChange = (field, value) => {
    setItemData({
      ...itemData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const filterItemLineExclusions = () => {
    return itemData.itemFamilyItemLines.map(familyItemLine => {
      let isExcluded = false;

      itemData.excludeItemLines.forEach(excludedItemLine => {
        if(excludedItemLine.season === familyItemLine.season && excludedItemLine.vehicleType === familyItemLine.vehicleType) {
          isExcluded = true;
        }
      });

      return {value: familyItemLine,
        label: `${itemLineNames.vehicleTypes[familyItemLine.vehicleType]} / ${itemLineNames.seasons[familyItemLine.season]}`,
        excluded: isExcluded
      }
    });

  }

  const filterCountryExclusions = () => {
    return itemData.itemFamilyMarkets.map(country => ({value: country, excluded: itemData.excludeMarkets.includes(country), label: t(`countries:${country}`)}));
  }

  const filterAttributeExclusions = (familyField, itemField, globalField) => {
    const itemValues = itemData[familyField];

    return itemValues.map(attribute => {
      let label = attribute;

      if(attributes[globalField]) {
        const row = attributes[globalField].find(item => item.id === attribute);
        if(row) {
          label = row.global_name;
        }
      }

      return {value: attribute, label: label, excluded: itemData[itemField].includes(attribute)}
    });
  };

  const handleDeleteListItem = (field, item) => {
    const newState = item.excluded ? itemData[field].filter(i => i !== item.value) : itemData[field].concat(item.value) ;
    handleChange(field, newState);
  }

  const cancelChanges = () => {
    setItemData(item);
    setHasUnsavedChanges(false);
  }

  const saveChanges = () => {
    // Save to server
    dispatch(saveItem({itemOwnerId, itemId, itemData}));
  }

  return (
    <>
      <Grid container spacing={7}>
        <Grid item xs={12} md={8}>
          <Box mb={2}>
            <Typography variant="subtitle1">{t('item:itemName')}</Typography>
            <Typography variant="h4" component="h1">{item.itemName}</Typography>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1">{t('item:itemCode')}</Typography>
            <Typography variant="h5">{item.itemCode}</Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="overline" display="block">{t('item:lastChanged')}</Typography>
            <Typography variant="caption">{item.updated}</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                label={t('item:itemFamily')}
                name="itemFamilyName"
                value={itemData.itemFamilyName}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemFamilyName')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('item:ean')}
                name="eanCode"
                value={itemData.eanCode}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('eanCode')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t('item:itemLifeCycle')}
                name="ean"
                value={itemData.itemLifeCycle}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemLifeCycle')}
              />
            </Grid>
          </Grid>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:itemLines')}</Typography></Box>
            <DeleteList
              items={filterItemLineExclusions()}
              onDelete={(item) => handleDeleteListItem('excludeItemLines', item)}
              disabled={fieldIsDisabled('excludeItemLines')}
            />
          </Box>


          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:markets')}</Typography></Box>
            <DeleteList
              items={filterCountryExclusions()}
              onDelete={(item) => handleDeleteListItem('excludeMarkets', item)}
              disabled={fieldIsDisabled('excludeMarkets')}
            />
          </Box>

          <Box my={3}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:attributes')}</Typography></Box>
            <DeleteList
              items={filterAttributeExclusions('itemFamilyAttributes', 'excludeAttributes', 'attributes')}
              onDelete={(item) => handleDeleteListItem('excludeAttributes', item)}
              disabled={fieldIsDisabled('excludeAttributes')}
            />
          </Box>

          <Box my={3}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:attributesGuarantee')}</Typography></Box>
            <DeleteList
              items={filterAttributeExclusions('itemFamilyAttributesGuarantee', 'excludeAttributesGuarantee', 'attributesGuarantee')}
              onDelete={(item) => handleDeleteListItem('excludeAttributesGuarantee', item)}
              disabled={fieldIsDisabled('excludeAttributesGuarantee')}
            />
          </Box>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:pricing')}</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label={t('item:price')}
                  name="price"
                  value={itemData.price || ''}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('price')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:priceDiscounted')}
                  name="priceDiscounted"
                  value={itemData.priceDiscounted || ''}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('priceDiscounted')}
                />
              </Grid>
            </Grid>
          </Box>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:technicalInfo')}</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label={t('item:structureCode')}
                  name="structureCode"
                  value={itemData.structureCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('structureCode')}
                />
              </Grid>

              <Grid item xs={6}>
                <BooleanField
                  label={t('item:runFlat')}
                  name="runFlat"
                  value={itemData.runFlat}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('runFlat')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:width')} (mm)`}
                  name="widthMm"
                  value={itemData.widthMm}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('widthMm')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:width')} (in)`}
                  name="widthIn"
                  value={itemData.widthIn}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('widthIn')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:aspectRatio')}
                  name="aspectRatio"
                  value={itemData.aspectRatio}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('aspectRatio')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:reinforcedCode')}
                  name="reinforcedCode"
                  value={itemData.reinforcedCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('reinforcedCode')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:weight')} (kg)`}
                  name="weightKg"
                  value={itemData.weightKg}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('weightKg')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:weight')} (lb)`}
                  name="weightLb"
                  value={itemData.weightLb}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('weightLb')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:rim')} (in)`}
                  name="rimIn"
                  value={itemData.rimIn}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('rimIn')}
                />
              </Grid>
            </Grid>
          </Box>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:euLabelInfo')}</Typography></Box>
            <Grid container spacing={3}>


              <Grid item xs={6}>
                <TextField
                  label={t('item:euDirectiveNumber')}
                  name="eUDirectiveNumberCode"
                  value={itemData.eUDirectiveNumberCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('eUDirectiveNumberCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:loadIndex')}
                  name="loadIndexCode"
                  value={itemData.loadIndexCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('loadIndexCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:loadIndex')} 2`}
                  name="loadIndex2Code"
                  value={itemData.loadIndex2Code}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('loadIndex2Code')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:speedIndex')}
                  name="speedIndexCode"
                  value={itemData.speedIndexCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('speedIndexCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('item:speedIndex')} 2`}
                  name="speedIndex2Code"
                  value={itemData.speedIndex2Code}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('speedIndex2Code')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:rollingResistance')}
                  name="rollingResistanceCode"
                  value={itemData.rollingResistanceCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('rollingResistanceCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:wetGrip')}
                  name="wetGripCode"
                  value={itemData.wetGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('wetGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:snowGrip')}
                  name="snowGripCode"
                  value={itemData.snowGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('snowGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:iceGrip')}
                  name="iceGripCode"
                  value={itemData.iceGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('iceGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:noisePerformance')}
                  name="noisePerformance"
                  value={itemData.noisePerformance}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('noisePerformance')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:noiseClass')}
                  name="noiseClass"
                  value={itemData.noiseClass}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('noiseClass')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:tyreClass')}
                  name="tyreClass"
                  value={itemData.tyreClass}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('tyreClass')}
                />
              </Grid>

            </Grid>
          </Box>

        </Grid>
        <Grid item xs={12} md={4}>
          <MainImage src={itemData.imageMain?.medium || itemData.itemFamilyImage?.medium} />
          <FilePicker
            fieldName={t('item:overrideImageMain')}
            fieldValue={itemData.imageMain}
            setFieldValue={(file) => handleChange('imageMain', file)}
            disabled={fieldIsDisabled('imageMain')}
          />
          <FilePicker viewOnly={true} fieldName={t('item:imageEuLabel')} fieldValue={itemData.imageEuLabel} />
        </Grid>
      </Grid>

      {hasUnsavedChanges ? <ActionBar cancelChanges={cancelChanges} saveChanges={saveChanges} disabled={saveStatus === 'loading'} /> : null}
    </>
  );


};

export default ItemPagePC1;
